import React, { useState } from 'react';
import { AlertTriangle, Settings, Send, AlertCircle, CheckCircle, Loader2, Trash2, Wrench, Search, ChevronDown, ChevronUp, Eye, Target, Check, Upload } from 'lucide-react';
import SimpleTranscribe from './SimpleTranscribe';
import useTranscription from '../hooks/useTranscription';
import { createIndustrialContext } from '../utils/contextLoader';

/**
 * EnhancedDefectAnalysisPage Component
 * 
 * Enhanced version of DefectAnalysisPage with:
 * - Interactive selection of defects, root causes, and corrective actions
 * - Final selection display and summary
 * - Submit functionality to send selections as JSON
 * - Modern, mobile-friendly UI with improved UX
 */
const EnhancedDefectAnalysisPage = () => {
  // Configuration state
  const [config, setConfig] = useState({
    baseUrl: 'https://5378c0ea3300.ngrok-free.app',
    submitUrl: 'https://5378c0ea3300.ngrok-free.app/submit-selection', // New submit endpoint
    agentName: 'jk_pilger_agent_v8',
    configPath: 'config/jk-gemba.yaml',
    sonioxUrl: 'https://voicefuncappv21150.azurewebsites.net',
    translateToEnglish: false,
    enableEndTags: true,
    enableHinglish: true,
    enableLanguageIdentification: false,
    enableSpeakerDiarization: false,
    maxNonFinalTokensDurationMs: 6000,
    useIndustrialContext: true,
    excludeArabicLanguages: true
  });

  // Transcription hook for voice input
  const {
    transcribedText,
    isRecording,
    error: transcriptionError,
    appendText,
    clearText,
    setRecordingState,
    setError: setTranscriptionError,
    clearError: clearTranscriptionError
  } = useTranscription();

  // Response and UI state
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [expandedDefects, setExpandedDefects] = useState({});

  // Selection state - Updated for paired selections
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [selectedPairs, setSelectedPairs] = useState([]); // Array of {rootCause, correctiveAction, isPrimary} pairs
  const [remarks, setRemarks] = useState(''); // User comments/remarks

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Handle configuration changes
  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle transcription updates
  const handleTranscriptionUpdate = (newText) => {
    appendText(newText);
    clearTranscriptionError();
    setError(null);
  };

  // Handle transcription errors
  const handleTranscriptionError = (err) => {
    setTranscriptionError(err);
  };

  // Handle recording state changes
  const handleRecordingStateChange = (recording) => {
    setRecordingState(recording);
  };

  // Handle manual text input changes
  const handleTextChange = (e) => {
    const newText = e.target.value;
    clearText();
    if (newText) {
      appendText(newText);
    }
  };

  // Clear all text and selections
  const handleClearText = () => {
    clearText();
    setError(null);
    clearTranscriptionError();
    // Clear selections when clearing text
    setSelectedDefect(null);
    setSelectedPairs([]);
    setRemarks('');
    setResponse(null);
    setSubmitSuccess(false);
    setSubmitError(null);
  };

  // Toggle defect expansion
  const toggleDefectExpansion = (index) => {
    setExpandedDefects(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Handle defect selection
  const handleDefectSelection = (defectIndex, defect) => {
    setSelectedDefect({ index: defectIndex, ...defect });
    // Clear pairs when defect changes and auto-select primary pair
    const primaryPair = {
      id: 'primary',
      rootCause: {
        root_cause_code: defect.root_cause_code,
        root_cause_text: defect.root_cause_text
      },
      correctiveAction: {
        action_code: defect.corrective_action_code,
        action_text: defect.corrective_action_text
      },
      isPrimary: true
    };
    setSelectedPairs([primaryPair]);
    setSubmitSuccess(false);
    setSubmitError(null);
    // Auto-expand the selected defect
    setExpandedDefects(prev => ({
      ...prev,
      [defectIndex]: true
    }));
  };

  // Handle pair selection (root cause + corrective action together)
  const handlePairSelection = (rootCause, correctiveAction, isPrimary = false, pairId = null) => {
    const newPair = {
      id: pairId || `${isPrimary ? 'primary' : 'alt'}-${Date.now()}`,
      rootCause,
      correctiveAction,
      isPrimary
    };

    setSelectedPairs(prev => {
      // Check if this pair already exists
      const existingIndex = prev.findIndex(pair => pair.id === newPair.id);

      if (existingIndex >= 0) {
        // Remove existing pair (toggle off)
        return prev.filter(pair => pair.id !== newPair.id);
      } else {
        // Add new pair
        return [...prev, newPair];
      }
    });

    setSubmitSuccess(false);
    setSubmitError(null);
  };

  // Check if a pair is selected
  const isPairSelected = (rootCauseCode, actionCode, isPrimary) => {
    return selectedPairs.some(pair =>
      pair.rootCause.root_cause_code === rootCauseCode &&
      (pair.correctiveAction.action_code === actionCode ||
       pair.correctiveAction.corrective_action_code === actionCode) &&
      pair.isPrimary === isPrimary
    );
  };

  // Handle API call for analysis
  const handleAnalyze = async () => {
    if (!transcribedText.trim()) {
      setError('Please enter a breakdown description or record your voice');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResponse(null);
    // Clear selections when starting new analysis
    setSelectedDefect(null);
    setSelectedPairs([]);
    setRemarks('');
    setSubmitSuccess(false);
    setSubmitError(null);

    try {
      const formData = new FormData();
      formData.append('agent_name', config.agentName);
      formData.append('input', `"${transcribedText}"`);
      formData.append('config_path', config.configPath);
      formData.append('raw_output', 'True');

      const apiResponse = await fetch(`${config.baseUrl}/worker/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          'ngrok-skip-browser-warning': 'true'
        }
      });

      if (!apiResponse.ok) {
        throw new Error(`API request failed: ${apiResponse.status} ${apiResponse.statusText}`);
      }

      const responseText = await apiResponse.text();
      
      // Handle the response which might contain ```json wrapper
      let jsonData;
      try {
        jsonData = JSON.parse(responseText);
      } catch (e) {
        const jsonMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/);
        if (jsonMatch) {
          jsonData = JSON.parse(jsonMatch[1]);
        } else {
          throw new Error('Invalid JSON response format');
        }
      }

      setResponse(jsonData);
    } catch (err) {
      setError(err.message || 'An error occurred while analyzing the input');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle final selection submission
  const handleSubmitSelection = async () => {
    if (!selectedDefect || selectedPairs.length === 0) {
      setSubmitError('Please select a defect and at least one root cause-corrective action pair before submitting');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const submissionData = {
        timestamp: new Date().toISOString(),
        original_input: transcribedText,
        remarks: remarks.trim(),
        selected_defect: {
          defect_code: selectedDefect.defect_code,
          defect_text: selectedDefect.defect_text,
          confidence_score: selectedDefect.confidence_score,
          mapping_status: selectedDefect.mapping_status,
          curator_action: selectedDefect.curator_action
        },
        selected_pairs: selectedPairs.map(pair => ({
          root_cause: {
            root_cause_code: pair.rootCause.root_cause_code,
            root_cause_text: pair.rootCause.root_cause_text,
            is_primary: pair.isPrimary
          },
          corrective_action: {
            action_code: pair.correctiveAction.action_code || pair.correctiveAction.corrective_action_code,
            action_text: pair.correctiveAction.action_text || pair.correctiveAction.corrective_action_text,
            is_primary: pair.isPrimary
          },
          pair_id: pair.id
        })),
        analysis_metadata: {
          agent_name: config.agentName,
          config_path: config.configPath,
          submission_source: 'enhanced_defect_analysis_page',
          total_pairs_selected: selectedPairs.length
        }
      };

      const submitResponse = await fetch(config.submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify(submissionData)
      });

      if (!submitResponse.ok) {
        throw new Error(`Submission failed: ${submitResponse.status} ${submitResponse.statusText}`);
      }

      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError(err.message || 'An error occurred while submitting the selection');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get confidence color
  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'text-green-400 bg-green-900/30';
    if (score >= 0.6) return 'text-yellow-400 bg-yellow-900/30';
    return 'text-red-400 bg-red-900/30';
  };

  // Get curator action color
  const getCuratorActionColor = (action) => {
    switch (action) {
      case 'AUTO_ACCEPT': return 'text-green-400 bg-green-900/30 border-green-500/30';
      case 'REVIEW_REQUIRED': return 'text-yellow-400 bg-yellow-900/30 border-yellow-500/30';
      case 'HUMAN_DECISION': return 'text-orange-400 bg-orange-900/30 border-orange-500/30';
      case 'REJECT': return 'text-red-400 bg-red-900/30 border-red-500/30';
      default: return 'text-blue-400 bg-blue-900/30 border-blue-500/30';
    }
  };

  // Get mapping status color
  const getMappingStatusColor = (status) => {
    if (status.startsWith('NEAR_MATCH')) return 'text-blue-400 bg-blue-900/30';
    if (status === 'NEW_ENTRY') return 'text-purple-400 bg-purple-900/30';
    if (status === 'EXACT_MATCH') return 'text-green-400 bg-green-900/30';
    return 'text-gray-400 bg-gray-900/30';
  };

  // Render final selection summary
  const renderFinalSelection = () => {
    if (!selectedDefect || selectedPairs.length === 0) {
      return null;
    }

    return (
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 backdrop-blur-sm rounded-xl border border-purple-500/30 p-6 mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
            <Check className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-white">Final Selection Summary</h3>
          <span className="text-sm bg-purple-500/30 text-purple-200 px-3 py-1 rounded-full">
            {selectedPairs.length} pair{selectedPairs.length !== 1 ? 's' : ''} selected
          </span>
        </div>

        {/* Selected Defect */}
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <h4 className="text-sm font-medium text-red-300">Selected Defect</h4>
          </div>
          <p className="text-xs text-red-200 mb-1">{selectedDefect.defect_code}</p>
          <p className="text-sm text-white mb-2">{selectedDefect.defect_text}</p>
          <div className={`inline-flex px-2 py-1 rounded text-xs ${getConfidenceColor(selectedDefect.confidence_score)}`}>
            {Math.round(selectedDefect.confidence_score * 100)}% confidence
          </div>
        </div>

        {/* Selected Pairs */}
        <div className="space-y-4 mb-6">
          <h4 className="text-lg font-medium text-white flex items-center space-x-2">
            <span>Selected Root Cause & Corrective Action Pairs</span>
          </h4>
          {selectedPairs.map((pair, index) => (
            <div key={pair.id} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white/5 rounded-lg border border-white/10">
              {/* Root Cause */}
              <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Search className="w-4 h-4 text-orange-400" />
                  <h5 className="text-sm font-medium text-orange-300">Root Cause {index + 1}</h5>
                  {pair.isPrimary && (
                    <span className="text-xs bg-orange-500/30 text-orange-200 px-2 py-1 rounded">Primary</span>
                  )}
                </div>
                <p className="text-xs text-orange-200 mb-1">{pair.rootCause.root_cause_code}</p>
                <p className="text-sm text-white">{pair.rootCause.root_cause_text}</p>
              </div>

              {/* Corrective Action */}
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Wrench className="w-4 h-4 text-blue-400" />
                  <h5 className="text-sm font-medium text-blue-300">Corrective Action {index + 1}</h5>
                  {pair.isPrimary && (
                    <span className="text-xs bg-blue-500/30 text-blue-200 px-2 py-1 rounded">Primary</span>
                  )}
                </div>
                <p className="text-xs text-blue-200 mb-1">
                  {pair.correctiveAction.action_code || pair.correctiveAction.corrective_action_code}
                </p>
                <p className="text-sm text-white">
                  {pair.correctiveAction.action_text || pair.correctiveAction.corrective_action_text}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Remarks Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-white mb-2">
            Additional Comments/Remarks (Optional)
          </label>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add any additional comments, observations, or context about your selection..."
            className="w-full h-24 p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent"
            maxLength={500}
          />
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-400">
              Optional field to provide additional context or notes
            </span>
            <span className="text-xs text-gray-400">
              {remarks.length}/500
            </span>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
          <div className="text-sm text-purple-200">
            Ready to submit your selection? This will send your choices to the system for processing.
          </div>
          <button
            onClick={handleSubmitSelection}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2 min-h-[44px]"
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Upload className="w-5 h-5" />
            )}
            <span>{isSubmitting ? 'Submitting...' : 'Submit Selection'}</span>
          </button>
        </div>

        {/* Submit Status */}
        {submitSuccess && (
          <div className="mt-4 p-3 bg-green-900/20 border border-green-500/30 rounded-lg flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300">Selection submitted successfully!</span>
          </div>
        )}

        {submitError && (
          <div className="mt-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300">{submitError}</span>
          </div>
        )}
      </div>
    );
  };

  // Render enhanced defect card with selection functionality
  const renderDefectCard = (defect, index) => {
    const isExpanded = expandedDefects[index];
    const isSelected = selectedDefect && selectedDefect.index === index;

    return (
      <div key={index} className={`bg-white/5 backdrop-blur-sm rounded-xl border overflow-hidden transition-all duration-200 ${
        isSelected ? 'border-purple-500/50 ring-2 ring-purple-500/30' : 'border-white/10 hover:border-white/20'
      }`}>
        {/* Header with Selection */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start space-x-4 flex-1">
              {/* Selection Radio Button */}
              <div className="mt-1">
                <input
                  type="radio"
                  id={`defect-${index}`}
                  name="defect-selection"
                  checked={isSelected}
                  onChange={() => handleDefectSelection(index, defect)}
                  className="w-5 h-5 text-purple-600 bg-black/20 border-white/20 focus:ring-purple-500/50 focus:ring-2"
                />
              </div>

              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  <span className="text-sm font-mono text-red-300 bg-red-900/30 px-3 py-1 rounded-lg">
                    {defect.defect_code}
                  </span>
                  <div className={`px-3 py-1 rounded-lg text-xs font-medium ${getConfidenceColor(defect.confidence_score)}`}>
                    {Math.round(defect.confidence_score * 100)}% confidence
                  </div>
                </div>
                <label htmlFor={`defect-${index}`} className="cursor-pointer">
                  <h3 className="text-xl font-semibold text-white mb-2 hover:text-purple-200 transition-colors">
                    {defect.defect_text}
                  </h3>
                </label>
                <div className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium ${getMappingStatusColor(defect.mapping_status)}`}>
                  <Target className="w-3 h-3 mr-1" />
                  {defect.mapping_status}
                </div>
              </div>
            </div>
          </div>

          {/* Primary Pair - Always Visible */}
          <div className="mb-4">
            <div className={`border rounded-lg p-4 transition-all duration-200 ${
              isSelected && isPairSelected(defect.root_cause_code, defect.corrective_action_code, true)
                ? 'bg-gradient-to-r from-orange-900/30 to-blue-900/30 border-purple-400/50 ring-2 ring-purple-400/30'
                : 'bg-gradient-to-r from-orange-900/20 to-blue-900/20 border-purple-500/30'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <Search className="w-4 h-4 text-orange-400" />
                    <Wrench className="w-4 h-4 text-blue-400" />
                  </div>
                  <h4 className="text-sm font-medium text-white">Primary Root Cause & Corrective Action Pair</h4>
                  <span className="text-xs bg-purple-500/30 text-purple-200 px-2 py-1 rounded">Recommended</span>
                </div>
                {isSelected && (
                  <input
                    type="checkbox"
                    checked={isPairSelected(defect.root_cause_code, defect.corrective_action_code, true)}
                    onChange={() => handlePairSelection(
                      {
                        root_cause_code: defect.root_cause_code,
                        root_cause_text: defect.root_cause_text
                      },
                      {
                        corrective_action_code: defect.corrective_action_code,
                        corrective_action_text: defect.corrective_action_text
                      },
                      true,
                      'primary'
                    )}
                    className="w-5 h-5 text-purple-600 bg-black/20 border-white/20 rounded focus:ring-purple-500/50 focus:ring-2"
                  />
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Root Cause */}
                <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <Search className="w-3 h-3 text-orange-400" />
                    <span className="text-xs font-medium text-orange-300">Root Cause</span>
                  </div>
                  <p className="text-xs text-orange-200 mb-1">{defect.root_cause_code}</p>
                  <p className="text-sm text-white">{defect.root_cause_text}</p>
                </div>

                {/* Corrective Action */}
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <Wrench className="w-3 h-3 text-blue-400" />
                    <span className="text-xs font-medium text-blue-300">Corrective Action</span>
                  </div>
                  <p className="text-xs text-blue-200 mb-1">{defect.corrective_action_code}</p>
                  <p className="text-sm text-white">{defect.corrective_action_text}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Curator Action */}
          <div className={`p-3 rounded-lg border mb-4 ${getCuratorActionColor(defect.curator_action)}`}>
            <div className="flex items-center space-x-2 mb-1">
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium">Curator Action: {defect.curator_action}</span>
            </div>
          </div>

          {/* Rationale */}
          <div className="bg-gray-900/30 border border-gray-500/30 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Analysis Rationale</h4>
            <p className="text-sm text-gray-100">{defect.rationale}</p>
          </div>

          {/* Expand/Collapse Button */}
          {(defect.possible_root_causes?.length > 0 || defect.possible_corrective_actions?.length > 0) && (
            <button
              onClick={() => toggleDefectExpansion(index)}
              className="flex items-center space-x-2 text-purple-300 hover:text-purple-200 transition-colors"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <span className="text-sm">
                {isExpanded ? 'Hide' : 'Show'} Alternative Options
              </span>
            </button>
          )}
        </div>

        {/* Expandable Alternative Pairs */}
        {isExpanded && (
          <div className="border-t border-white/10 p-6 bg-white/5">
            <h4 className="text-sm font-medium text-purple-300 mb-4 flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <Search className="w-4 h-4" />
                <Wrench className="w-4 h-4" />
              </div>
              <span>Alternative Root Cause & Corrective Action Pairs</span>
            </h4>

            <div className="space-y-4">
              {/* Generate alternative pairs intelligently */}
              {defect.possible_root_causes?.length > 0 && defect.possible_corrective_actions?.length > 0 && (
                <>
                  {/* Create pairs from alternatives - intelligent pairing */}
                  {defect.possible_root_causes.map((cause, causeIndex) =>
                    defect.possible_corrective_actions.map((action, actionIndex) => {
                      const pairId = `alt-${causeIndex}-${actionIndex}`;
                      const isSelectedPair = isPairSelected(cause.root_cause_code, action.action_code, false);

                      return (
                        <div key={pairId} className={`border rounded-lg p-4 transition-all duration-200 ${
                          isSelected && isSelectedPair
                            ? 'bg-gradient-to-r from-orange-900/30 to-blue-900/30 border-purple-400/50 ring-1 ring-purple-400/30'
                            : 'bg-gradient-to-r from-orange-900/10 to-blue-900/10 border-purple-500/20'
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <div className="flex items-center space-x-1">
                                <Search className="w-3 h-3 text-orange-400" />
                                <Wrench className="w-3 h-3 text-blue-400" />
                              </div>
                              <span className="text-sm font-medium text-white">
                                Alternative Pair {causeIndex + 1}.{actionIndex + 1}
                              </span>
                            </div>
                            {isSelected && (
                              <input
                                type="checkbox"
                                checked={isSelectedPair}
                                onChange={() => handlePairSelection(cause, action, false, pairId)}
                                className="w-4 h-4 text-purple-600 bg-black/20 border-white/20 rounded focus:ring-purple-500/50 focus:ring-2"
                              />
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Root Cause */}
                            <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3">
                              <div className="flex items-center space-x-2 mb-2">
                                <Search className="w-3 h-3 text-orange-400" />
                                <span className="text-xs font-medium text-orange-300">Root Cause</span>
                              </div>
                              <p className="text-xs text-orange-200 mb-1">{cause.root_cause_code}</p>
                              <p className="text-sm text-white">{cause.root_cause_text}</p>
                            </div>

                            {/* Corrective Action */}
                            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                              <div className="flex items-center space-x-2 mb-2">
                                <Wrench className="w-3 h-3 text-blue-400" />
                                <span className="text-xs font-medium text-blue-300">Corrective Action</span>
                              </div>
                              <p className="text-xs text-blue-200 mb-1">{action.action_code}</p>
                              <p className="text-sm text-white">{action.action_text}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </>
              )}

              {/* Fallback if no alternatives available */}
              {(!defect.possible_root_causes?.length || !defect.possible_corrective_actions?.length) && (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">No alternative pairs available for this defect.</p>
                  <p className="text-xs mt-1">The primary pair above is the recommended solution.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 pt-20">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-600 to-orange-500 rounded-2xl mb-4">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Enhanced Defect Analysis
            </h1>
            <p className="text-xl text-purple-100">
              AI-powered defect detection with interactive selection and submission
            </p>
          </div>

          {/* Configuration Panel */}
          <div className="mb-6">
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="flex items-center space-x-2 text-white hover:text-purple-200 transition-colors"
            >
              <Settings className="w-5 h-5" />
              <span>Configuration</span>
            </button>

            {showConfig && (
              <div className="mt-4 bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Defect Analysis API URL
                    </label>
                    <input
                      type="text"
                      value={config.baseUrl}
                      onChange={(e) => handleConfigChange('baseUrl', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="https://api.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Submit Selection URL
                    </label>
                    <input
                      type="text"
                      value={config.submitUrl}
                      onChange={(e) => handleConfigChange('submitUrl', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="https://api.example.com/submit-selection"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Rayosense Voice API URL
                    </label>
                    <input
                      type="text"
                      value={config.sonioxUrl}
                      onChange={(e) => handleConfigChange('sonioxUrl', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="https://voice-api.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Agent Name
                    </label>
                    <input
                      type="text"
                      value={config.agentName}
                      onChange={(e) => handleConfigChange('agentName', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="jk_pilger_agent_v2"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Config Path
                    </label>
                    <input
                      type="text"
                      value={config.configPath}
                      onChange={(e) => handleConfigChange('configPath', e.target.value)}
                      className="w-full p-3 bg-black/20 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="config/jk-gemba.yaml"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
            {/* Input Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-white mb-2">
                Defect Description
              </label>

              {/* Controls Section */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  {/* Transcribe Component */}
                  <SimpleTranscribe
                    onTranscriptionUpdate={handleTranscriptionUpdate}
                    onError={handleTranscriptionError}
                    onRecordingStateChange={handleRecordingStateChange}
                    size="lg"
                    sonioxUrl={config.sonioxUrl}
                    translateToEnglish={config.translateToEnglish}
                    enableEndTags={config.enableEndTags}
                    languageHints={config.enableHinglish ? ["hi", "en"] : ["en"]}
                    enableLanguageIdentification={false}
                    enableSpeakerDiarization={config.enableSpeakerDiarization}
                    maxNonFinalTokensDurationMs={config.maxNonFinalTokensDurationMs}
                    context={config.useIndustrialContext ? createIndustrialContext(config.enableHinglish) : null}
                  />

                  {/* Status Indicator */}
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      isRecording ? 'bg-red-400 animate-pulse' : 'bg-gray-400'
                    }`} />
                    <span className={`text-sm font-medium ${
                      isRecording ? 'text-red-300' : 'text-gray-300'
                    }`}>
                      {isRecording ? 'Recording...' : 'Ready'}
                    </span>
                  </div>
                </div>

                {/* Clear Button */}
                <button
                  onClick={handleClearText}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-300 rounded-lg transition-colors duration-200"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Clear</span>
                </button>
              </div>

              <div className="space-y-4">
                <textarea
                  value={transcribedText}
                  onChange={handleTextChange}
                  placeholder={
                    isRecording
                      ? config.enableHinglish
                        ? "Listening... Describe the defect in English, Hindi, or mixed (Hinglish)!"
                        : "Listening... Describe the defect!"
                      : config.enableHinglish
                        ? "Describe the defect (e.g., 'Bearing overheating' or 'Bearing garam ho raha hai') or click the microphone to record..."
                        : "Describe the defect (e.g., Bearing overheating) or click the microphone to record..."
                  }
                  className="w-full h-32 p-4 bg-black/20 border border-white/10 rounded-xl text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent"
                />
                <div className="flex justify-center">
                  <button
                    onClick={handleAnalyze}
                    disabled={isLoading || !transcribedText.trim()}
                    className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-red-600 to-orange-500 text-white rounded-xl font-medium hover:from-red-700 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-red-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2 min-h-[44px]"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    <span>{isLoading ? 'Analyzing...' : 'Analyze Defect'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {(error || transcriptionError) && (
              <div className="mb-6 space-y-2">
                {error && (
                  <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <span className="text-red-300">{error}</span>
                  </div>
                )}
                {transcriptionError && (
                  <div className="p-4 bg-orange-900/20 border border-orange-500/30 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-orange-400" />
                    <span className="text-orange-300">Transcription Error: {transcriptionError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Final Selection Summary */}
            {renderFinalSelection()}

            {/* Response Display */}
            {response && Array.isArray(response) && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span>Defect Analysis Results ({response.length})</span>
                  </h3>
                  <div className="text-sm text-purple-200">
                    Select a defect, then choose root cause and corrective action
                  </div>
                </div>
                <div className="space-y-6">
                  {response.map((defect, index) => renderDefectCard(defect, index))}
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="mt-6 space-y-4">
              <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                <h3 className="text-sm font-medium text-purple-300 mb-2">How to use Enhanced Defect Analysis with Paired Selection:</h3>
                <ul className="text-sm text-purple-200 space-y-1">
                  <li>• Describe the defect manually or use voice recording</li>
                  <li>• Click "Analyze Defect" to get AI-powered analysis results</li>
                  <li>• Select one defect by clicking the radio button next to it</li>
                  <li>• <strong>Primary pair is auto-selected</strong> - the recommended root cause & corrective action combination</li>
                  <li>• Use checkboxes to select/deselect the primary pair or add alternative pairs</li>
                  <li>• Expand "Alternative Options" to see more root cause & corrective action combinations</li>
                  <li>• <strong>Select multiple pairs</strong> when you're uncertain which approach will work best</li>
                  <li>• Review all selected pairs in the summary section</li>
                  <li>• Click "Submit Selection" to send your choices to the system</li>
                  <li>• Configure submission URL in settings if needed</li>
                </ul>
              </div>

              <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <h3 className="text-sm font-medium text-blue-300 mb-2">Why Paired Selection?</h3>
                <ul className="text-sm text-blue-200 space-y-1">
                  <li>• <strong>Root causes and corrective actions work together</strong> - they're naturally paired</li>
                  <li>• <strong>Multiple approaches</strong> - when you find a defect, you might not know which solution will work</li>
                  <li>• <strong>Comprehensive coverage</strong> - select multiple pairs to ensure all possibilities are considered</li>
                  <li>• <strong>Industrial reality</strong> - maintenance teams often try multiple solutions in sequence</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDefectAnalysisPage;
